import { Fragment, useEffect, useMemo, useRef } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { SpinnerCircular } from "spinners-react";
import { ShowField } from "../../components/ViewApplication/ViewApplication";
import { useSetStuffToRenderOnNavbar } from "../../contexts/NavbarCommonSpace";
import { isLevel1 } from "../../hoc/isLevel1";
import {
  useAllFields,
  useApplicationFormSteps,
} from "../../hooks/applications.hooks";
import { useApplicationForLevel1 } from "../../hooks/level-1-application-approval.hooks";
import BoxComponent from "../../components/CommonComponents/BoxComponent/BoxComponent";
import html2pdf from 'html2pdf.js';

export const ViewApplicationForLevel1 = isLevel1(() => {
  const location = useLocation();
  const navigate = useNavigate();
  const id = location.state?.id;

  const contentRef = useRef(null);
  // Ref to the content to be captured for the PDF
  const handleDownloadPDF = () => {
    // Get the element to generate the PDF from
    const content = contentRef.current;
    // Set PDF options
    const options = {
      margin: 1,
      filename: 'page.pdf',
      image: {
        type: 'jpeg',
        quality: 0.98
      },
      html2canvas: {
        scale: 2
      },
      jsPDF: {
        unit: 'in',
        format: 'letter',
        orientation: 'portrait'
      }
    };
    // const updatedData = `<div><p>Application Id</p>${content}</div>`
    // Generate and download the PDF
    html2pdf().from(content).set(options).save();
  };

  const {
    isError: isError1,
    isFetching: isFetching1,
    data: application,
  } = useApplicationForLevel1({ id: String(id) });

  const {
    data: result,
    isError: isError2,
    isFetching: isFetching2,
  } = useApplicationFormSteps(Number(application?.name_of_service));

  const {
    isFetching: isFetching3,
    isError: isError3,
    data: allFields,
  } = useAllFields({
    serviceName: String(application?.name_of_service || ""),
  });

  const steps = result?.data || [];
  const isFetching = isFetching1 || isFetching2;

  const selectedFields = useMemo(
    () =>
      (steps || [])
        .filter((e) => !["Documents", "NOC"].includes(e.type))
        .flatMap((e) => e.fields || [])
        .reduce<Map<string, true>>((acc, e) => acc.set(e.uid, true), new Map()),
    [steps]
  );

  const isError =
    isError1 || isError2 || (!isFetching && (!application || !steps?.length));

  const setStuff = useSetStuffToRenderOnNavbar();

  useEffect(() => {
    setStuff(
      <div className="md:ml-auto md:mr-8">
        <p className="text-grey-600">
          <span className="w-28 font-semibold inline-block">
            Application ID
          </span>
          <span className="text-grey-600 font-semibold mx-2">:</span>
          <span>{application?.applicationId || "Nil"}</span>
        </p>
        <p className="text-grey-600 flex items-center">
          <span className="w-28 text-grey-600 font-semibold inline-block">
            Status
          </span>
          <span className="text-grey-600 font-semibold mx-2">:</span>
          <span className="text-primary">{application?.status || "Nil"}</span>
        </p>
      </div>
    );
    return () => setStuff(null);
  }, [setStuff, application]);

  if (isNaN(+String(id))) {
    return <Navigate to={"/user-applications"} replace />;
  }

  if (isFetching || isError) {
    return (
      <div className="flex justify-center items-center p-8">
        {isError ? (
          <p>Something went wrong while fetching info!</p>
        ) : (
          <SpinnerCircular />
        )}
      </div>
    );
  }



  const PrintData = () => {
    return <div style={{ display: 'none' }}>
      <div ref={contentRef}>
        <h4 className="text-center text-grey-600 font-semibold mb-8 text-2xl">
          Application
        </h4>
        <div className="flex flex-col ml-auto mr-8 justify-items-end items-end">
          <p className="text-grey-600">
            <span className="w-28 font-semibold inline-block">
              Application ID
            </span>
            <span className="text-grey-600 font-semibold mx-2">:</span>
            <span>{application?.applicationId || "Nil"}</span>
          </p>
          <p className="text-grey-600 flex items-center">
            <span className="text-grey-600 font-semibold inline-block">
              Status
            </span>
            <span className="text-grey-600 font-semibold mx-2">:</span>
            <span className="text-primary">{application?.status || "Nil"}</span>
          </p>
        </div>
        <ApplicationList />
      </div>
    </div>
  }


  const ApplicationList = () => {
    let i = 0;
    return <div>
      {(allFields || [])
        .filter((e) => selectedFields.has(e.uid) || !!application?.[e.uid])
        .map((e) => (
          <ShowField
            field={e}
            index={++i}
            key={`field-${e.id}`}
            data={application || {}}
          />
        ))}
    </div>
  }

  return (
    <div className="px-4 pb-4 md:p-4">
      <div className="bg-white p-8">
        <h4 className="text-center text-grey-600 font-semibold mb-8 text-2xl">
          Application
        </h4>
        <ApplicationList />
        <PrintData />
        <div className="d-flex gap-5 justify-center items-center mt-4">

          <button className="bg-black ml-4 px-6 py-2 font-bold text-white rounded-full disabled:bg-gray-300" onClick={() => navigate('/level-1-applications')}>Back</button>
          <button
            type="button"
            onClick={handleDownloadPDF}
            className="bg-primary px-6 py-2 rounded-full font-bold text-white"
          >
            Download
          </button>
          <button
            type="button"
            onClick={() =>
              navigate("/level-1-verify-application", { state: location.state })
            }
            className="bg-primary px-6 py-2 rounded-full font-bold text-white"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
});
